import { ACTIONS, type Action, type Permission, RESOURCES, type Resource } from './actions';

export const PERMISSIONS = {
  userRead: 'user:read',
  userCreate: 'user:create',
  userUpdate: 'user:update',
  userManage: 'user:manage',

  roleRead: 'role:read',
  roleManage: 'role:manage',

  groupRead: 'group:read',
  groupManage: 'group:manage',

  applicationRead: 'application:read',
  applicationManage: 'application:manage',

  clientRead: 'client:read',
  clientManage: 'client:manage',

  sowRead: 'sow:read',
  sowCreate: 'sow:create',
  sowUpdate: 'sow:update',
  sowManage: 'sow:manage',

  projectRead: 'project:read',
  projectCreate: 'project:create',
  projectUpdate: 'project:update',
  projectDelete: 'project:delete',
  projectManage: 'project:manage',
  /** Decide someone else's project, and skip the queue with your own. */
  projectApprove: 'project:approve',

  allocationRead: 'allocation:read',
  allocationCreate: 'allocation:create',
  allocationUpdate: 'allocation:update',
  /** Remove a row entered by mistake, such as a duplicate. Held through allocation:manage (PMO, delivery manager). */
  allocationDelete: 'allocation:delete',
  allocationManage: 'allocation:manage',

  /** The global list of roles an allocation can carry. */
  resourceRoleRead: 'resource-role:read',
  resourceRoleManage: 'resource-role:manage',

  /** Open a signed download link for one of a project's uploaded documents. */
  documentRead: 'document:read',
  /** Upload a governing or SOW document. */
  documentCreate: 'document:create',
  documentManage: 'document:manage',

  milestoneRead: 'milestone:read',
  milestoneManage: 'milestone:manage',

  taskRead: 'task:read',
  taskCreate: 'task:create',
  taskUpdate: 'task:update',
  taskDelete: 'task:delete',
  taskManage: 'task:manage',

  candidateRead: 'candidate:read',
  candidateCreate: 'candidate:create',
  candidateUpdate: 'candidate:update',
  /** Verify a candidate's document and approve a stage of their onboarding. */
  candidateApprove: 'candidate:approve',
  /** Take, transfer or release any candidate and move a whole caseload. Not implied by manage. */
  candidateAssign: 'candidate:assign',
  candidateManage: 'candidate:manage',

  /** Reveal a sensitive answer, such as a social security number. Each reveal is logged. */
  candidateSensitiveRead: 'candidate-sensitive:read',

  /** Candidate documents are identity papers, so they are not gated on project `document:*`. */
  candidateDocumentRead: 'candidate-document:read',
  candidateDocumentManage: 'candidate-document:manage',

  formTemplateRead: 'form-template:read',
  formTemplateManage: 'form-template:manage',

  emailTemplateRead: 'email-template:read',
  emailTemplateManage: 'email-template:manage',

  employeeRead: 'employee:read',
  employeeManage: 'employee:manage',

  workflowRead: 'workflow:read',
  workflowManage: 'workflow:manage',

  directoryRead: 'directory:read',
  directoryManage: 'directory:manage',

  directNdaRead: 'direct-nda:read',
  directNdaCreate: 'direct-nda:create',
  /** Cancel, reissue, retry and sign as HR. */
  directNdaUpdate: 'direct-nda:update',
  /** Who signs Direct NDAs for HR. Reading the setting needs `direct-nda:read`. */
  directNdaSettingsUpdate: 'direct-nda-settings:update',

  questionnaireRead: 'questionnaire:read',
  questionnaireCreate: 'questionnaire:create',
  questionnaireUpdate: 'questionnaire:update',
  /** Archives; a published version is never deleted. */
  questionnaireDelete: 'questionnaire:delete',
  questionnaireManage: 'questionnaire:manage',

  trainingGroupRead: 'training-group:read',
  trainingGroupCreate: 'training-group:create',
  trainingGroupUpdate: 'training-group:update',
  trainingGroupDelete: 'training-group:delete',
  trainingGroupManage: 'training-group:manage',

  trainingAssignmentRead: 'training-assignment:read',
  trainingAssignmentCreate: 'training-assignment:create',
  trainingAssignmentUpdate: 'training-assignment:update',
  trainingAssignmentManage: 'training-assignment:manage',

  /**
   * Grade other people's answers and see everyone's results. Taking a
   * questionnaire is not a permission: it is the caller's own session.
   */
  trainingAttemptApprove: 'training-attempt:approve',

  /** Every certificate, and the verify check. A person's own are not gated on this. */
  certificateRead: 'certificate:read',
  certificateManage: 'certificate:manage',

  trainingSettingsRead: 'training-settings:read',
  trainingSettingsUpdate: 'training-settings:update',
  trainingSettingsManage: 'training-settings:manage',
} as const satisfies Record<string, Permission>;

export const ALL_PERMISSIONS = Object.values(PERMISSIONS) as Permission[];

const IMPLIED_BY_MANAGE: Action[] = [
  ACTIONS.read,
  ACTIONS.create,
  ACTIONS.update,
  ACTIONS.delete,
  ACTIONS.approve,
];

/** `<resource>:manage` implies every other action on that resource. */
export const expandPermissions = (granted: readonly Permission[]): Set<Permission> => {
  const expanded = new Set<Permission>(granted);

  for (const entry of granted) {
    const [resource, action] = entry.split(':') as [Resource, Action];
    if (action !== ACTIONS.manage) continue;

    for (const implied of IMPLIED_BY_MANAGE) {
      expanded.add(`${resource}:${implied}`);
    }
  }

  return expanded;
};

/**
 * The inverse of `expandPermissions`: drops every action a `manage` on the same
 * resource already implies. `hasPermission` expands at check time, so the two
 * forms grant exactly the same access; this one is what travels.
 *
 * It matters because the list rides in the access token, and the token rides
 * in a cookie. Expanded, a platform administrator's token pushed the sign-in
 * response past the 4 KB a proxy buffers for headers by default, so every
 * administrator's sign-in answered 502, and each new resource moved it toward
 * the 4096 bytes at which a browser drops the cookie without a word.
 */
export const compactPermissions = (granted: readonly Permission[]): Permission[] => {
  const unique = [...new Set(granted)];
  const managed = new Set(
    unique
      .filter((entry) => entry.endsWith(`:${ACTIONS.manage}`))
      .map((entry) => entry.split(':')[0]),
  );

  return unique.filter((entry) => {
    const [resource, action] = entry.split(':') as [Resource, Action];

    return !(managed.has(resource) && IMPLIED_BY_MANAGE.includes(action));
  });
};

export const hasPermission = (
  granted: readonly Permission[],
  required: readonly Permission[],
): boolean => {
  const expanded = expandPermissions(granted);
  return required.every((entry) => expanded.has(entry));
};

export const permissionsForResource = (resource: Resource): Permission[] =>
  ALL_PERMISSIONS.filter((entry) => entry.startsWith(`${resource}:`));

export const isPermission = (value: string): value is Permission => {
  const [resource, action] = value.split(':');
  return (
    Object.values(RESOURCES).includes(resource as Resource) &&
    Object.values(ACTIONS).includes(action as Action)
  );
};
